import DealScoreBlock from './DealScoreBlock'
import MetricCard from './MetricCard'
import { formatCompactMoneyParts, formatPercent, isNetLoss } from './format'

// Tooltip copy: node 8763:2480, "METRICS" block -- Deal Score's own entry lives beside its
// component in DealScoreBlock.jsx, since that block renders outside this container (spec 4.6).
// Total Investment's sentence follows Cong's text in letter 115.
const TOOLTIPS = {
  netProfit:
    'Estimated profit after all projected deal expenses are deducted from the after-repair value (ARV).  [ARV - Total Expenses]',
  totalInvestment:
    'Estimated cash you’ll put into the deal over the project, after accounting for financing and projected costs.  [Acquisition Costs + Rehab Costs + Closing Costs + Holding Costs + Marketing Costs]',
  roi: 'Estimated profit as a percentage of total projected expenses.  [Net Profit ÷ Total Expenses]',
  annualizedRoi:
    'Estimated ROI expressed as a yearly rate, based on the projected deal timeline.  [ROI × (12 ÷ Project Months)]',
  returnOnRehab:
    'Estimated profit as a percentage of the renovation budget.  [Net Profit ÷ Rehab Costs Example: $50K profit ÷ $100K rehab budget = 50%]',
}

// Letter 99 (6.3.2): the strip's percentages are whole (20.20% -> 20%). The trailing '%' is split
// off so digits and sign render at the two sizes spec 2.3 draws (36/800 digits, 28/800 sign).
function splitPercent(n) {
  const full = formatPercent(n, 0)
  return full == null ? { prefix: null, body: null, suffix: null } : { prefix: null, body: full.slice(0, -1), suffix: '%' }
}

// Letter 99 (6.3.1): compact, as the design draws it ($47K, "$" and "K" one size down). A loss keeps
// its sign, now ahead of the "$" (-$12K) -- see README.md ("Deal Metrics rounding").
function splitMoneyValue(n) {
  const parts = formatCompactMoneyParts(n)
  return parts == null ? { prefix: null, body: null, suffix: null } : { prefix: `${parts.sign}$`, body: parts.body, suffix: parts.suffix }
}

// Letter 99 (5.5): hover only -- the box holds no editable field. Deal Score's block keeps its own look.
const STRIP_RING_CLS = 'outline outline-1 -outline-offset-1 outline-brand-fieldBdr hover:outline-[#9FB497]'

// Frame 400: two siblings, not six cards -- DealScoreBlock (140, own band fill) and this 846-wide
// white container holding the five metric cards. See README.md ("Deal Score is 0-100...") and
// spec 2.1: rendering Deal Score as a sixth card inside the container is wrong on three counts.
export default function MetricsStrip({ computed }) {
  // Round once, feed the same rounded value to both the label classification and the displayed
  // figure -- otherwise a value like -0.4 reads as Net Loss (raw < 0) beside a displayed "$0"
  // (formatMoney's own rounding), the same fork DealScoreBlock resolved for its band vs display.
  // isNetLoss (format.js, shared with Flip Summary's Net Profit row) applies the same Math.round
  // itself, so the classification holds even for a caller that passes the raw figure.
  const rawNetProfit = computed?.netProfit
  const netProfit = Number.isFinite(rawNetProfit) ? Math.round(rawNetProfit) : rawNetProfit
  const netLoss = isNetLoss(netProfit)

  const cards = [
    {
      key: 'netProfit',
      testId: 'Net Profit (Metrics)',
      label: netLoss ? 'Net Loss' : 'Net Profit',
      tooltip: TOOLTIPS.netProfit,
      negative: netLoss,
      ...splitMoneyValue(netProfit),
    },
    {
      key: 'totalInvestment',
      testId: 'Total Investment (Metrics)',
      label: 'Total Investment',
      tooltip: TOOLTIPS.totalInvestment,
      ...splitMoneyValue(computed?.totalInvestment),
    },
    { key: 'roi', testId: 'ROI (Metrics)', label: 'ROI', tooltip: TOOLTIPS.roi, ...splitPercent(computed?.roi) },
    {
      key: 'annualizedRoi',
      testId: 'Annualized ROI (Metrics)',
      label: 'Annualized ROI',
      tooltip: TOOLTIPS.annualizedRoi,
      ...splitPercent(computed?.annualizedRoi),
    },
    {
      key: 'returnOnRehab',
      testId: 'Return on Rehab (Metrics)',
      label: 'Return on Rehab',
      tooltip: TOOLTIPS.returnOnRehab,
      ...splitPercent(computed?.returnOnRehab),
    },
  ]

  return (
    // Sticky is the outer element on purpose: nesting it inside the overflow-x-auto row below would
    // anchor it to that row's own scroll container instead of the page, silently killing the stick.
    <div className="sticky top-0 z-20">
      {/* xl:, not md:/lg: -- the row needs >=1046px to fit, a threshold neither breakpoint clears.
          See README.md ("the metrics strip reverts its overflow wrapper at xl, not md/lg").
          tabIndex -1: Chrome makes a scroll container with no Tab stop inside one itself (README.md,
          "Tab walks the fields only"); mouse, wheel and touch still scroll it. */}
      <div tabIndex={-1} className="flex items-start gap-x-[12px] overflow-x-auto xl:overflow-visible outline-none">
        {/* computed.dealScore is already 0-100 (reilm-be c9da48c) -- passed through unscaled; null or
            absent falls to DealScoreBlock's own Unavailable state. README.md ("Deal Score is 0-100"). */}
        <DealScoreBlock score={computed?.dealScore} />
        {/* outline, not border -- a CSS border on a border-box element eats 2px of the content
            height MetricCard's fixed h-[88px] needs. Same fix as SectionCard.jsx's own comment. No
            vertical padding: each card sets its own 14px (letter 99, 7.4.1). */}
        <div className={`flex items-stretch gap-x-[12px] h-[88px] bg-white rounded-[8px] pl-[26px] pr-[2px] shrink-0 ${STRIP_RING_CLS}`}>
          {cards.map((card, i) => (
            <MetricCard
              key={card.key}
              testId={card.testId}
              label={card.label}
              tooltip={card.tooltip}
              prefix={card.prefix}
              body={card.body}
              suffix={card.suffix}
              negative={card.negative}
              withDivider={i < cards.length - 1}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
